import React from 'react';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { canonicalRoutePath } from '../../util/routes';
import { organizationRef, siteRootUrl, websiteRef } from '../../util/siteStructuredData';
import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import { validProps } from './Field';

import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';

/**
 * @param {Object} meta page asset meta (pageTitle, pageDescription, socialSharing)
 * @param {string} schemaType schema.org type of the page
 * @param {Object} fieldOptions custom field components
 * @param {Object} [site]
 * @param {string} [site.url] canonical URL of the page
 * @param {string} [site.marketplaceRootURL] for the @id references to the site nodes
 * @returns {Object} title, description, schema and socialSharing for <Page>
 */
export const getMetadata = (meta, schemaType, fieldOptions, site = {}) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};
  const { url, marketplaceRootURL } = site;
  const urlMaybe = url ? { url } : {};
  // The site nodes are described in full on the home page (util/siteStructuredData.js); here they
  // are referenced. The About page is about the organization itself.
  const hasSiteRoot = !!siteRootUrl(marketplaceRootURL);
  const isPartOfMaybe = hasSiteRoot ? { isPartOf: websiteRef(marketplaceRootURL) } : {};
  const mainEntityMaybe =
    hasSiteRoot && schemaType === 'AboutPage'
      ? { mainEntity: organizationRef(marketplaceRootURL) }
      : {};

  // Schema for search engines (helps them to understand what this page is about)
  // https://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
  const pageSchemaForSEO = {
    '@context': 'https://schema.org',
    '@type': schemaType || 'WebPage',
    ...urlMaybe,
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
    ...isPartOfMaybe,
    ...mainEntityMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

//////////////////
// Page Builder //
//////////////////

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @param {Object} props.pageAssetsData
 * @param {Array<Object>} props.pageAssetsData.sections
 * @param {Object} props.pageAssetsData.meta
 * @param {Object} props.pageAssetsData.meta.pageTitle
 * @param {Object} props.pageAssetsData.meta.pageDescription
 * @param {Object} props.pageAssetsData.meta.socialSharing
 * @param {boolean?} props.inProgress
 * @param {Object?} props.error
 * @param {ReactNode?} props.fallbackPage
 * @param {string} props.schemaType type from schema.org (e.g. 'Article', 'Website')
 * @param {string?} props.currentPage name of the current page based on route configuration
 * @param {Object} props.options
 * @param {Object<string,FieldComponentConfig>} props.options.fieldComponents custom field components
 * @returns {JSX.Element} page component
 */
const PageBuilder = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    featuredListings,
    ...pageProps
  } = props;

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  const marketplaceRootURL = config?.marketplaceRootURL;
  const canonicalUrl =
    marketplaceRootURL && routeConfiguration && location
      ? `${siteRootUrl(marketplaceRootURL)}${canonicalRoutePath(routeConfiguration, location)}`
      : null;
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents, {
    url: canonicalUrl,
    marketplaceRootURL,
  });

  const layoutAreas = `
    main
    footer
  `;
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <header className={css.topbar}>
        <TopbarContainer currentPage={currentPage} />
      </header>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Main, Footer } = props;
          return (
            <>
              <Main as="main" id="main-content" className={css.main}>
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <div className={css.sectionFlow}>
                    <SectionBuilder
                      sections={sections}
                      options={{ ...options, featuredListings }}
                    />
                  </div>
                )}
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
